import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Menu, X, ExternalLink } from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import { useLanguage } from '../context/LanguageContext';

// Affiliate LifeWave Shop Link
const LIFEWAVE_SHOP_URL = 'https://lifewave.com/ioanaalexandra/store/products';

const navTranslations = {
  en: { myStory: "My Story", contact: "Contact", journal: "Journal", guides: "Guides" },
  ro: { myStory: "Povestea Mea", contact: "Contact", journal: "Jurnal", guides: "Ghiduri" },
  es: { myStory: "Mi Historia", contact: "Contacto", journal: "Diario", guides: "Guías" },
  it: { myStory: "La Mia Storia", contact: "Contatto", journal: "Diario", guides: "Guide" },
  de: { myStory: "Meine Geschichte", contact: "Kontakt", journal: "Tagebuch", guides: "Leitfäden" }
};

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t, language } = useLanguage();
  const navT = navTranslations[language] || navTranslations['en'];

  return (
    <nav className="sticky top-0 z-50 glass border-b border-slate-200/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center">
              <span className="text-white font-bold text-lg">LW</span>
            </div>
            <span className="font-bold text-lg text-slate-900 hidden sm:block">Live Well~Live Good~Live Long</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link 
              to="/" 
              className="text-slate-600 hover:text-slate-900 font-medium transition-colors"
              data-testid="nav-home"
            >
              {t('home')}
            </Link>
            <Link 
              to="/products" 
              className="text-slate-600 hover:text-slate-900 font-medium transition-colors"
              data-testid="nav-products"
            >
              {t('products')}
            </Link>
            <Link 
              to="/my-story" 
              className="text-slate-600 hover:text-slate-900 font-medium transition-colors"
              data-testid="nav-my-story"
            >
              {navT.myStory}
            </Link>
            <Link 
              to="/videos" 
              className="text-slate-600 hover:text-slate-900 font-medium transition-colors"
              data-testid="nav-videos"
            >
              Video
            </Link>
            <Link 
              to="/jurnal" 
              className="text-slate-600 hover:text-slate-900 font-medium transition-colors"
              data-testid="nav-journal"
            >
              {navT.journal}
            </Link>
            <Link 
              to="/ghiduri" 
              className="text-slate-600 hover:text-slate-900 font-medium transition-colors"
              data-testid="nav-guides"
            >
              {navT.guides}
            </Link>
            <Link 
              to="/contact" 
              className="text-slate-600 hover:text-slate-900 font-medium transition-colors"
              data-testid="nav-contact"
            >
              {navT.contact}
            </Link>
            
            <LanguageSelector />
            
            <Button 
              className="bg-sky-600 hover:bg-sky-700 text-white rounded-full"
              onClick={() => window.open(LIFEWAVE_SHOP_URL, '_blank')}
              data-testid="nav-shop"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              {t('shopNow')}
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-2">
            <LanguageSelector />
            <button 
              className="p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200">
            <div className="flex flex-col gap-4">
              <Link 
                to="/" 
                className="text-slate-600 hover:text-slate-900 font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('home')}
              </Link>
              <Link 
                to="/products" 
                className="text-slate-600 hover:text-slate-900 font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('products')}
              </Link>
              <Link 
                to="/my-story" 
                className="text-slate-600 hover:text-slate-900 font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                {navT.myStory}
              </Link>
              <Link 
                to="/videos" 
                className="text-slate-600 hover:text-slate-900 font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Video
              </Link>
              <Link 
                to="/jurnal" 
                className="text-slate-600 hover:text-slate-900 font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                {navT.journal}
              </Link>
              <Link 
                to="/ghiduri" 
                className="text-slate-600 hover:text-slate-900 font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                {navT.guides}
              </Link>
              <Link 
                to="/contact" 
                className="text-slate-600 hover:text-slate-900 font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                {navT.contact}
              </Link>
              <Button 
                className="bg-sky-600 hover:bg-sky-700 text-white rounded-full w-full"
                onClick={() => { window.open(LIFEWAVE_SHOP_URL, '_blank'); setMobileMenuOpen(false); }}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                {t('shopNow')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
